import { useState, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'
import type { MediaFolder, MediaImage } from '@/types'

export function useMediaFolders() {
  return useQuery<MediaFolder[]>({
    queryKey: ['media-folders'],
    queryFn: () => api.get('/media-folders'),
  })
}

export function useCreateFolder() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { name: string; parentId?: string | null; tags?: string[]; type?: 'library' | 'cuts' }) =>
      api.post<{ ok: boolean; folder: MediaFolder }>('/media-folders', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['media-folders'] }),
  })
}

export function useUpdateFolder() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ folderId, name, tags }: { folderId: string; name?: string; tags?: string[] }) =>
      api.put<{ ok: boolean }>(`/media-folders/${folderId}`, { name, tags }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['media-folders'] }),
  })
}

export const useRenameFolder = useUpdateFolder

export function useDeleteFolder() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (folderId: string) =>
      api.delete<{ ok: boolean }>(`/media-folders/${folderId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['media-folders'] })
      qc.invalidateQueries({ queryKey: ['storage-usage'] })
    },
  })
}

export function useUploadToFolder() {
  const qc = useQueryClient()
  const [uploadProgress, setUploadProgress] = useState(0)
  const fileCountRef = useRef(0)

  const mutation = useMutation({
    mutationFn: ({ folderId, files }: { folderId: string; files: File[] }) => {
      fileCountRef.current = files.length
      setUploadProgress(0)
      return api.uploadMultipleWithProgress<{ ok: boolean; added: number; images: MediaImage[] }>(
        files,
        `/media-folders/${folderId}/upload`,
        (percent) => setUploadProgress(percent),
      )
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['media-folders'] })
      qc.invalidateQueries({ queryKey: ['storage-usage'] })
      setUploadProgress(0)
    },
  })

  return { ...mutation, uploadProgress, fileCount: fileCountRef.current }
}

export function useDeleteImage() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ folderId, imageId }: { folderId: string; imageId: string }) =>
      api.delete<{ ok: boolean }>(`/media-folders/${folderId}/images/${imageId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['media-folders'] })
      qc.invalidateQueries({ queryKey: ['storage-usage'] })
    },
  })
}

export function useBulkDeleteImages() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ folderId, imageIds }: { folderId: string; imageIds: string[] }) =>
      api.post<{ ok: boolean; deleted: number }>(`/media-folders/${folderId}/images/bulk-delete`, { imageIds }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['media-folders'] })
      qc.invalidateQueries({ queryKey: ['storage-usage'] })
    },
  })
}

export function useRenameImage() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ folderId, imageId, filename }: { folderId: string; imageId: string; filename: string }) =>
      api.put<{ ok: boolean }>(`/media-folders/${folderId}/images/${imageId}`, { filename }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['media-folders'] }),
  })
}

export function useUpdateFolderStatus() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ folderId, status }: { folderId: string; status: 'active' | 'scheduled' | 'published' }) =>
      api.put<{ ok: boolean }>(`/media-folders/${folderId}/status`, { status }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['media-folders'] }),
  })
}

export function useMoveImages() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ imageIds, targetFolderId }: { imageIds: string[]; targetFolderId: string }) =>
      api.post<{ ok: boolean; moved: number }>('/media-folders/move-images', { imageIds, targetFolderId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['media-folders'] }),
  })
}

export function useMoveFolder() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ folderId, parentId }: { folderId: string; parentId: string | null }) =>
      api.put<{ ok: boolean }>(`/media-folders/${folderId}/move`, { parentId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['media-folders'] }),
  })
}

export function useBulkAntiFingerprint() {
  return useMutation({
    mutationFn: ({ folderId, imageIds }: { folderId: string; imageIds: string[] }) =>
      api.post<{ ok: boolean; jobId: string; imageCount: number }>(
        `/media-folders/${folderId}/images/bulk-anti-fingerprint`,
        { imageIds },
      ),
  })
}

export function useAntiFingerprintProgress(jobId: string | null) {
  return useQuery<{ status: string; total: number; processed: number; failed: number }>({
    queryKey: ['afp-progress', jobId],
    queryFn: () => api.get(`/anti-fingerprint/progress/${jobId}`),
    enabled: !!jobId,
    refetchInterval: (query) => {
      const data = query.state.data
      if (data?.status === 'done') return false
      return 1500 // poll every 1.5s while processing
    },
  })
}

export function useFolderStats(folderId: string | null) {
  return useQuery<{
    totalImages: number
    usageByPage: Record<string, { used: number; available: number; accountName: string }>
  }>({
    queryKey: ['folder-stats', folderId],
    queryFn: () => api.get(`/media-folders/${folderId}/stats`),
    enabled: !!folderId,
  })
}
